import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';

import { Button } from '@common/components/Button';
import { MessageBox } from '@common/components/MessageBox';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Loading } from '@common/components/loading';
import { getConfig, getDuplicatedPedigrees, getDuplicatedIndividuals, updateConfig } from './actions/main';
import { UserSecuritySettings } from './components/user-security-settings';
import { DatabaseOptions } from './components/database-options';
import { FolderSecuritySettings } from './components/folder-security-settings';
import { InfoMessage } from '@common/components/info-message';

import './database-security.styles.scss';

export const MODULE_NAME = 'DATABASE_SECURITY';

export class DatabaseSecurity extends React.Component {
  state = {
    databaseProperties: {},
    databaseSecurity: [],
    userGroups: {},
    loading: false,
    submitting: false,
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'small',
      onCustomOk: () => {},
    }
  };

  componentDidMount() {
    this.getConfig();
  }

  getConfig = async () => {
    this.setState((state) => ({ ...state, loading: true }));
    let { userGroups, databaseSecurity, ...otherConfig } = await getConfig();

    userGroups = userGroups.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});

    databaseSecurity = databaseSecurity.reduce((acc, item) => {
      acc[item.secIndex] = item;
      return acc;
    }, {});

    this.setState((state) => ({ ...state, ...otherConfig, userGroups, databaseSecurity, loading: false }));
  }

  handleSave = async () => {
    const { close } = this.props;
    const { databaseProperties, databaseSecurity } = this.state;
    try {
      this.setState((state) => ({ ...state, submitting: true })); 
      const submitData = {
        databaseProperties,
        databaseSecurity: Object.values(databaseSecurity),
      };
  
      await updateConfig(submitData);
      this.setMessageProps({
        header: I18n.t('Information Message'),
        content: <InfoMessage>{I18n.t('Database Security Settings Saved')}</InfoMessage>,
        show: true,
        confirmText: 'Ok',
        onCustomOk: close,
      });
    } catch (err) {
      this.setMessageProps({
        header: I18n.t('Warning'),
        text: I18n.t('Failed to save data'),
        show: true,
        confirmText: 'Ok',
        onCustomOk: this.closeMessage,
      });
      console.error(err);
      this.setState((state) => ({ ...state, submitting: false })); 
    }
  }

  handleDatabasePropertiesChange = (databaseProperties) => {
    this.setState((x) => ({ ...x, databaseProperties }));
  }

  handleDatabaseSecurityChange = (databaseSecurity) => {
    this.setState((x) => ({ ...x, databaseSecurity }));
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        size: 'small',
        ...props,
      },
    }));
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  render() {
    const { submitting, loading, databaseProperties, userGroups, databaseSecurity } = this.state;
    const { close } = this.props;
    
    return (
      <Modal
        show
        animation={false}
        className="DatabaseSecurity"
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="DatabaseSecurity"
        onEscapeKeyDown={close}
        onHide={close}
      >
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...this.state.messageProps}
        />
        <ModalHeader closeButton onHide={close}>
          <ModalTitle {...getTestAttributes(MODULE_NAME, 'modal', 'title')}>{I18n.t('Database Security Settings')}</ModalTitle>
        </ModalHeader>
        {loading && (
          <Flex justify="center" alignItems="center" className="DatabaseSecurity__content">
            <Loading size={25} />
          </Flex>
        )}
        {!loading && (
          <>
            <ModalBody className="DatabaseSecurity__content">
              <UserSecuritySettings 
                value={databaseProperties} 
                className="DatabaseSecurity__content__section" 
                onChange={this.handleDatabasePropertiesChange} 
                setMessageProps={this.setMessageProps}
              />
              <DatabaseOptions 
                value={databaseProperties} 
                className="DatabaseSecurity__content__section" 
                getDuplicatedIndividuals={getDuplicatedIndividuals}
                getDuplicatedPedigrees={getDuplicatedPedigrees}
                onChange={this.handleDatabasePropertiesChange} 
              />
              <FolderSecuritySettings 
                className="DatabaseSecurity__content__section" 
                groups={userGroups} 
                databaseSecurity={databaseSecurity}
                onDatabaseSecurityChange={this.handleDatabaseSecurityChange}
              />
            </ModalBody>
            <ModalFooter>
              <Button {...getTestAttributes(MODULE_NAME, 'cancel', 'button')} disabled={submitting} onClick={close}>
                {I18n.t('Cancel')}
              </Button>
              <Button
                {...getTestAttributes(MODULE_NAME, 'submit', 'button')}
                bsStyle="primary"
                disabled={submitting}
                onClick={this.handleSave}
              >
                {I18n.t(submitting ? 'Loading' : 'Save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    );
  }
}